import { FileArchive, FileText, Film, Folder, Image, Music } from 'lucide-react'
import type { ComponentType } from 'react'
import type { LucideProps } from 'lucide-react'

import { formatBytes } from '../../lib/format'
import type { SharedAsset } from '../../portal/types'
import { ICON_STROKE } from '../../ui'

const KIND_ICON: Record<SharedAsset['kind'], ComponentType<LucideProps>> = {
  image: Image,
  video: Film,
  audio: Music,
  document: FileText,
  archive: FileArchive,
  other: Folder,
}

/**
 * La entrega leída como una lista de nombres, no como una retícula.
 *
 * Es la cara de `projects.type = 'documents'`, y la diferencia no es estética.
 * En un juego de planos, doce miniaturas de página en blanco son doce
 * rectángulos indistinguibles: lo que dice cuál es cuál es
 * «planta-baja-rev3.pdf». El nombre ES el contenido, así que manda el nombre y
 * la miniatura pasa a ser el acompañante pequeño.
 *
 * Se ordenan por nombre desde el servidor, que es donde está el orden que el
 * estudio puso al numerarlos.
 */
export function DocumentList({
  assets,
  onOpen,
}: {
  assets: SharedAsset[]
  onOpen: (asset: SharedAsset) => void
}) {
  return (
    <ul className="mx-auto flex w-full max-w-page flex-col divide-y divide-line px-4 sm:px-6">
      {assets.map((asset) => {
        const KindIcon = KIND_ICON[asset.kind]

        return (
          <li key={asset.id}>
            <button
              type="button"
              onClick={() => onOpen(asset)}
              className="action flex w-full items-center gap-3 py-3 text-left"
            >
              <div className="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded bg-surface-sunken text-ink-subtle">
                {asset.thumb_url !== null ? (
                  <img
                    src={asset.thumb_url}
                    alt=""
                    loading="lazy"
                    className="size-full object-cover"
                  />
                ) : (
                  <KindIcon size={20} strokeWidth={ICON_STROKE} aria-hidden />
                )}
              </div>

              {/* `break-all` y no `truncate`: en un nombre de plano la parte que
                  distingue una revisión de otra está al FINAL, y recortarlo por
                  la derecha esconde justo lo que hay que leer. */}
              <span className="min-w-0 flex-1">
                <span className="block break-all text-body text-ink">{asset.filename}</span>
                <span className="block text-label text-ink-subtle" data-numeric>
                  {formatBytes(asset.size_bytes)}
                </span>
              </span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}
